import { useState } from "react";
import { AuthenticatedLayout } from "../../components/layout/AuthenticatedLayout";
import { useNavigate } from "react-router-dom";
import { cn } from "@/lib/utils";

export default function EventsPage() {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<"all" | "yours">("all");

  // Define event type for typing purposes (will be used when implementing event features)
  // type EventItem = {
  //   id: number;
  //   name: string;
  //   date: string;
  //   time: string;
  //   location: string;
  //   image: string;
  //   organizer: string;
  // };

  // User's events (empty for demonstration - will be implemented later)
  // const userEvents: EventItem[] = [];

  const handleCreateEvent = () => {
    navigate("/events/create");
  };

  return (
    <AuthenticatedLayout
      showBottomNav={true}
      showNavbar={false}
      backgroundColor="#FBF6F1"
    >
      <div className="w-full min-h-screen bg-[#FBF6F1] flex flex-col">
        {/* Header */}
        <div className="w-full h-14 p-4 inline-flex justify-between items-center">
          <div
            className="size-7 relative overflow-hidden"
            onClick={() => navigate(-1)}
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="28"
              height="28"
              viewBox="0 0 28 28"
              fill="none"
            >
              <path
                d="M17.5 7L10.5 14L17.5 21"
                stroke="#1E3A3A"
                strokeWidth="1.2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </div>
          <div className="justify-start text-[#1E3A3A] text-2xl font-medium font-manrope leading-7">
            Sự kiện
          </div>
          <div className="size-7 relative overflow-hidden opacity-0">
            {/* Placeholder for alignment */}
          </div>
        </div>

        {/* Search bar */}
        <div className="self-stretch px-4 py-2 flex flex-col justify-start items-start gap-2.5">
          <div className="self-stretch p-3 bg-[#FBF6F1] rounded-md inline-flex justify-start items-center gap-2">
            <div className="size-6 relative overflow-hidden">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
              >
                <path
                  d="M21 21L16.65 16.65M19 11C19 15.4183 15.4183 19 11 19C6.58172 19 3 15.4183 3 11C3 6.58172 6.58172 3 11 3C15.4183 3 19 6.58172 19 11Z"
                  stroke="#1E3A3A"
                  strokeWidth="1.2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </div>
            <div className="justify-start text-[#6F6F6B] text-sm font-normal font-sora leading-none">
              Tìm kiếm sự kiện
            </div>
          </div>
        </div>

        {/* Tab Navigation */}
        <div className="w-full inline-flex justify-start items-center">
          <div
            className={cn(
              "flex-1 p-4 flex justify-center items-center gap-2.5",
              activeTab === "all" ? "border-b border-[#1E3A3A]" : "",
            )}
            onClick={() => setActiveTab("all")}
          >
            <div
              className={cn(
                "text-center justify-start text-sm font-sora leading-none",
                activeTab === "all"
                  ? "text-[#1E3A3A] font-medium"
                  : "text-[#6F6F6B] font-normal",
              )}
            >
              Tất cả sự kiện
            </div>
          </div>
          <div
            className={cn(
              "flex-1 p-4 flex justify-center items-center gap-2.5",
              activeTab === "yours" ? "border-b border-[#1E3A3A]" : "",
            )}
            onClick={() => setActiveTab("yours")}
          >
            <div
              className={cn(
                "text-center justify-start text-sm font-sora leading-none",
                activeTab === "yours"
                  ? "text-[#1E3A3A] font-medium"
                  : "text-[#6F6F6B] font-normal",
              )}
            >
              Sự kiện của bạn
            </div>
          </div>
        </div>

        {/* Empty State */}
        <div className="w-full py-6 flex flex-col justify-start items-center gap-6">
          <div className="w-40 h-40 flex items-center justify-center">
            <img
              src="public/icons/no_event.svg"
              alt="No events"
              className="w-32 h-32"
              onError={(e) => {
                e.currentTarget.src =
                  "https://placehold.co/128x128?text=No+Events";
              }}
            />
          </div>
          <div className="self-stretch text-center justify-start text-[#6F6F6B] text-sm font-normal font-sora leading-none">
            Hiện chưa có sự kiện nào
          </div>

          {/* Add Event Button - Only shown in "yours" tab */}
          {activeTab === "yours" && (
            <div
              data-hierarchy="primary"
              data-show-icon-line-left="true"
              data-show-icon-line-right="false"
              data-size="medium"
              className="px-4 py-3.5 bg-[#E06448] rounded-full inline-flex justify-center items-center gap-2.5 cursor-pointer"
              onClick={handleCreateEvent}
            >
              <div className="size-5 relative overflow-hidden">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="20"
                  height="20"
                  viewBox="0 0 20 20"
                  fill="none"
                >
                  <path
                    fill-rule="evenodd"
                    clip-rule="evenodd"
                    d="M10 4.375C10.3452 4.375 10.625 4.65482 10.625 5V9.375H15C15.3452 9.375 15.625 9.65482 15.625 10C15.625 10.3452 15.3452 10.625 15 10.625H10.625V15C10.625 15.3452 10.3452 15.625 10 15.625C9.65482 15.625 9.375 15.3452 9.375 15V10.625H5C4.65482 10.625 4.375 10.3452 4.375 10C4.375 9.65482 4.65482 9.375 5 9.375H9.375V5C9.375 4.65482 9.65482 4.375 10 4.375Z"
                    fill="#F4E7DA"
                  />
                </svg>
              </div>
              <div className="text-center justify-start text-[#F4E7DA] text-sm font-medium font-sora leading-none">
                Thêm sự kiện
              </div>
            </div>
          )}
        </div>
      </div>
    </AuthenticatedLayout>
  );
}
